import type { FC } from 'react';

import { ClickableDiv } from '@wwe-portal/ui/components/button';
import { cn } from '@wwe-portal/ui/lib/lib';

export interface FaqItemProps {
  id: string;
  answer: React.ReactNode;
  question: React.ReactNode;
  expanded: boolean;
  className?: string;
  onToggle?: () => void;
}

export const FaqItem: FC<FaqItemProps> = ({
  question,
  answer,
  expanded,
  className,
  onToggle,
  id,
}) => {
  return (
    <div
      className={cn(
        `bg-back300 flex flex-col items-start justify-center rounded-[10px] px-[17px] py-[26px]`,
        className
      )}
      data-faq-id={id}
    >
      <ClickableDiv
        className="flex w-full flex-[0_0_auto] items-center justify-center gap-[10px] self-stretch"
        onClick={onToggle}
      >
        <p className="font-poppins text-text400 mb-0 flex-1 text-sm font-semibold leading-[21px] tracking-[0]">
          {question}
        </p>

        {!expanded && (
          <svg
            fill="none"
            height="26"
            viewBox="0 0 26 26"
            width="26"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect className="fill-bgColor3" height="26" rx="13" width="26" />
            <path
              d="M12.75 5.93a6.57 6.57 0 00-2.625.531 6.815 6.815 0 00-2.148 1.445 6.814 6.814 0 00-1.446 2.149A6.57 6.57 0 006 12.68c0 .927.177 1.802.531 2.625a6.815 6.815 0 001.446 2.148 6.813 6.813 0 002.148 1.445 6.569 6.569 0 002.625.532c.927 0 1.802-.177 2.625-.532a6.812 6.812 0 002.148-1.445 6.815 6.815 0 001.446-2.148 6.57 6.57 0 00.531-2.625 6.57 6.57 0 00-.531-2.625 6.815 6.815 0 00-1.446-2.149 6.814 6.814 0 00-2.148-1.445 6.57 6.57 0 00-2.625-.531zm0 2.812a.71.71 0 01.531.227.731.731 0 01.219.523v2.438h2.406c.198 0 .373.073.524.219a.711.711 0 01.226.53.71.71 0 01-.226.532.731.731 0 01-.524.219H13.5v2.406a.731.731 0 01-.219.524.71.71 0 01-.531.226.71.71 0 01-.531-.226.731.731 0 01-.219-.524V13.43H9.562a.731.731 0 01-.523-.219.71.71 0 01-.226-.531c0-.209.075-.386.226-.531a.731.731 0 01.524-.22H12V9.493c0-.198.073-.372.219-.523a.71.71 0 01.531-.227z"
              fill="#0DD39C"
            />
          </svg>
        )}

        {expanded ? (
          <div className="bg-bgColor3 h-[26px] w-[26px] rounded-[13px]">
            <div className="left-[6px] top-[5px] h-4 w-[15px]">
              <svg
                fill="none"
                height="26"
                viewBox="0 0 26 26"
                width="26"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  className="fill-bgColor3"
                  height="26"
                  rx="13"
                  width="26"
                />
                <path
                  clipRule="evenodd"
                  d="M13.002 19.6553C16.7299 19.6553 19.752 16.6332 19.752 12.9053C19.752 9.17735 16.7299 6.15527 13.002 6.15527C9.27403 6.15527 6.25195 9.17735 6.25195 12.9053C6.25195 16.6332 9.27403 19.6553 13.002 19.6553ZM9.83594 12.1553C9.42172 12.1553 9.08594 12.4911 9.08594 12.9053C9.08594 13.3195 9.42172 13.6553 9.83594 13.6553L16.168 13.6553C16.5822 13.6553 16.918 13.3195 16.918 12.9053C16.918 12.4911 16.5822 12.1553 16.168 12.1553L9.83594 12.1553Z"
                  fill="#9309E2"
                  fillRule="evenodd"
                />
              </svg>
            </div>
          </div>
        ) : null}
      </ClickableDiv>

      <div
        className={cn(
          'font-poppins text-text400 transition-max-h w-fit overflow-hidden text-xs font-normal leading-5 duration-300 ease-in-out',
          {
            'max-h-0': !expanded,
            'max-h-screen': expanded,
          }
        )}
      >
        <div className="mt-5">{answer}</div>
      </div>
    </div>
  );
};
